import { Injectable } from '@angular/core'
import {
  AppointmentModel,
  ChargeFormService,
  EligibilityBenefits,
  errorMessage,
  Estimate,
  EstimateBalance,
  EstimateBalanceWorkflowStatus,
  EstimateCharge,
  EstimateData,
  EstimateFormGroup,
  EstimateTemplateModel,
  EstimateVisit,
  FileService,
  Patient,
  PatientInsurance,
} from '@mmx/shared'
import { catchError, filter, forkJoin, Observable, of, throwError } from 'rxjs'
import { finalize, map, switchMap, tap } from 'rxjs/operators'

import { SendContactEditDialogResult, SendContactEditDialogService } from '../../send-contact-edit-dialog'
import { BalanceEventsService } from './balance-events.service'
import { BenefitSlot, syncBenefitsIntoInputs } from './benefits-sync'
import {
  EstimateBalanceApiService,
  EstimateCalculateResult,
  SendBalanceMessageResult,
  SendBalanceWhat,
} from './estimate-balance-api.service'
import { BenefitsFormGroup, EstimateBalanceFormService } from './estimate-balance-form.service'
import { EstimateBalanceStateService } from './estimate-balance-state.service'
import { EstimateTemplateLoaderService } from './estimate-template-loader.service'

export type SendLetterChannel = 'email' | 'sms'

@Injectable()
export class EstimateBalanceFlowService {
  private form: EstimateFormGroup | null = null
  private benefitsForm: BenefitsFormGroup | null = null
  // Last appointment set the server has persisted for this estimate. Appointment edits are local
  // until Calculate (see updateAppointmentSelection / saveAndCalculate); this baseline lets
  // saveAndCalculate detect a pending change that must be committed via POST /appointments.
  private persistedAppointmentIds: string[] = []

  constructor(
    private readonly api: EstimateBalanceApiService,
    private readonly state: EstimateBalanceStateService,
    private readonly formService: EstimateBalanceFormService,
    private readonly templateService: EstimateTemplateLoaderService,
    private readonly chargeFormService: ChargeFormService,
    private readonly fileService: FileService,
    private readonly sendContactEditDialog: SendContactEditDialogService,
    private readonly balanceEvents: BalanceEventsService,
  ) {}

  attachForm(form: EstimateFormGroup): void {
    this.form = form
  }

  // Benefits form is the source of truth for inputs; without it the save can omit them and the server NaN→500s.
  attachBenefitsForm(form: BenefitsFormGroup): void {
    this.benefitsForm = form
  }

  hydrate(estimateId: string | null, draftSeed?: Partial<EstimateData>): Observable<EstimateData | null> {
    if (!estimateId || estimateId === 'new') {
      // hydrate('new') can refire for the same draft (e.g. an appointment change); capture
      // inputs first — EstimateInsuranceLoader can't resync hasSecondary later since its dedup
      // key never changes across unsaved drafts.
      const previousDraft = this.state.estimate()
      const carryOverInputs = previousDraft?.id == null ? previousDraft?.inputs : null
      const draft = this.createDraftEstimate(draftSeed)
      this.state.resetForNewEstimate(draft.patientId || null)
      // Gate on secondaryInsurance surviving the reset (same patient) to avoid leaking hasSecondary across patients.
      if (draft.inputs && carryOverInputs && this.state.secondaryInsurance()) {
        draft.inputs = {
          ...draft.inputs,
          hasSecondary: carryOverInputs.hasSecondary,
          secondaryInsuranceCoversCoinsurance: carryOverInputs.secondaryInsuranceCoversCoinsurance,
          secondaryInsuranceCoversCopayment: carryOverInputs.secondaryInsuranceCoversCopayment,
          secondaryInsuranceCoversDeductible: carryOverInputs.secondaryInsuranceCoversDeductible,
        }
      }
      this.persistedAppointmentIds = [...(draft.appointmentIds ?? [])]
      this.state.setEstimate(draft)
      this.state.setPatientId(draft.patientId || null)
      this.state.setSelectedAppointmentIds(draft.appointmentIds ?? [])
      if (this.form) {
        this.formService.patchEstimateForm(this.form, draft)
      }
      return of(draft)
    }
    this.state.startLoading()
    return this.api.getEstimate(estimateId).pipe(
      switchMap(estimate => this.loadBalanceAndVisits(estimate).pipe(
        tap(({ balance, visits }) => {
          this.persistedAppointmentIds = [...(estimate.appointmentIds ?? [])]
          this.state.hydrated(estimate, balance, visits)
          this.state.setUnscheduled(estimate.mode === Estimate.MODE.UNSCHEDULED)
          if (this.form) {
            this.formService.patchEstimateForm(this.form, estimate)
          }
        }),
        switchMap(() => estimate.modifySnapshot != null
          ? this.refreshBenefitsForSelection()
          : of(null)),
        map(() => estimate),
      )),
      catchError(err => {
        this.state.markError(errorMessage(err, 'balance.error'))
        return throwError(() => err)
      }),
    )
  }

  selectPrimaryInsurance(insurance: PatientInsurance | null): Observable<EligibilityBenefits | null> {
    const previousId = this.state.primaryInsurance()?.id ?? this.state.estimate()?.insurances?.P?.insuranceId
    const policyChanged = (previousId ?? null) !== (insurance?.id ?? null)
    this.state.setPrimaryInsurance(insurance)
    return this.refreshBenefits('primary', insurance, policyChanged)
  }

  selectSecondaryInsurance(insurance: PatientInsurance | null): Observable<EligibilityBenefits | null> {
    const previousId = this.state.secondaryInsurance()?.id ?? this.state.estimate()?.insurances?.S?.insuranceId
    const policyChanged = (previousId ?? null) !== (insurance?.id ?? null)
    this.state.setSecondaryInsurance(insurance)
    return this.refreshBenefits('secondary', insurance, policyChanged)
  }

  updateAppointmentSelection(appointmentIds: string[]): void {
    // Cannot-mix-modes invariant: a non-empty selection clears a stale unscheduled flag.
    // Empty selection is a no-op (picker's clear emits []).
    if (appointmentIds.length > 0 && this.state.unscheduled()) {
      this.setUnscheduledMode(false)
    }
    // Course of Care: an appointment's position in the date-sorted list IS its visit number. When
    // the set changes, shift each charge's visitNumbers to follow its appointment (mirrors the
    // backend renumber that POST /appointments does) so the form stays valid while the edit is
    // still local. A charge whose only visit was removed is left with no visit number, surfacing
    // the existing "requires a visit number" validation so the user resolves it before Calculate.
    if (this.form && this.state.cocMode()) {
      const remap = this.buildVisitRemap(this.state.selectedAppointmentIds(), appointmentIds)
      this.formService.remapChargeVisitNumbers(this.form, remap)
    }
    this.state.setSelectedAppointmentIds(appointmentIds)
    const estimate = this.state.estimate()
    if (estimate) {
      this.state.setEstimate({ ...estimate, appointmentIds: [...appointmentIds] })
    }
    if (this.form) {
      this.formService.setAppointmentIds(this.form, appointmentIds)
      this.autofillFacilityFromAppointments(appointmentIds)
    }
    this.state.markDirty()
  }

  setUnscheduledMode(on: boolean): void {
    this.state.setUnscheduled(on)
    if (on) {
      this.state.setSelectedAppointmentIds([])
      if (this.form) {
        this.formService.setAppointmentIds(this.form, [])
      }
    } else if (this.form) {
      // Leaving CoC clears every charge's visitNumbers — they're meaningless outside CoC.
      for (const charge of this.form.controls.charges.controls) {
        charge.controls.visitNumbers.setValue([])
      }
    }
    this.state.markDirty()
  }

  // Commits a Course-of-Care appointment-set change to the backend (POST /estimates/{id}/appointments
  // — the only endpoint allowed to mutate CoC appointments; PUT rejects it). Called from
  // saveAndCalculate at Calculate time, NOT on dropdown toggle, so a transient failure here is
  // cleanly retryable (nothing is half-applied behind the user's back). Deliberately does NOT
  // patch the form charges from the response — the form already holds the user's locally
  // renumbered/edited charges, which the subsequent save() persists.
  private commitAppointmentChange(appointmentIds: string[]): Observable<EstimateData> {
    const estimate = this.state.estimate()
    if (!estimate?.id) {
      return throwError(() => new Error('Cannot sync appointments without an estimate id'))
    }
    return this.api.syncAppointments(estimate.id, appointmentIds).pipe(
      tap(saved => {
        this.persistedAppointmentIds = [...(saved.appointmentIds ?? [])]
        this.state.setEstimate({ ...saved, charges: estimate.charges })
      }),
    )
  }

  calculate(): Observable<EstimateCalculateResult> {
    const estimateId = this.state.estimate()?.id
    if (!estimateId) {
      return throwError(() => new Error('Cannot calculate without an estimate id'))
    }
    return this.api.calculate(estimateId).pipe(
      tap(result => {
        this.state.setEstimate(result.estimate)
        this.persistedAppointmentIds = [...(result.estimate.appointmentIds ?? [])]
        this.state.setBalance(result.balance)
        this.state.clearDirty()
        // Recalculate (re)creates the persisted balance — refresh the patient balance panels.
        this.balanceEvents.notifyBalancesChanged()
      }),
    )
  }

  // Date-sorted appointment position → visit number, for both the old and new selection, diffed
  // by appointmentId. Returns `oldVisitNumber → newVisitNumber | null` (null = visit removed);
  // identity mappings are omitted so callers can short-circuit on an empty map.
  private buildVisitRemap(oldIds: string[], newIds: string[]): Map<number, number | null> {
    const appointments = this.state.appointments()
    const startAtMs = (id: string): number => {
      // startAt is a Moment; .valueOf() yields epoch ms (mirrors state.selectedAppointments).
      const startAt = appointments.find(a => a.id === id)?.startAt
      return Number(startAt?.valueOf() ?? 0)
    }
    const sortByStart = (ids: string[]): string[] => [...ids].sort((a, b) => startAtMs(a) - startAtMs(b))
    const newPositionById = new Map<string, number>()
    sortByStart(newIds).forEach((id, index) => newPositionById.set(id, index + 1))

    const remap = new Map<number, number | null>()
    sortByStart(oldIds).forEach((id, index) => {
      const oldNumber = index + 1
      const newNumber = newPositionById.get(id)
      if (newNumber == null) {
        remap.set(oldNumber, null)
      } else if (newNumber !== oldNumber) {
        remap.set(oldNumber, newNumber)
      }
    })
    return remap
  }

  private appointmentIdsEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false
    }
    const setB = new Set(b)
    return a.every(id => setB.has(id))
  }

  // Default facility to the appointment's locationId — empty facilityId 500s on Facility.primaryKey.get.
  private autofillFacilityFromAppointments(appointmentIds: string[]): void {
    if (!this.form || this.formService.getFacility(this.form)) {
      return
    }

    const appointments = this.state.appointments()
    const locationId = appointmentIds
      .map(id => appointments.find(a => a.id === id)?.locationId)
      .find((loc): loc is string => Boolean(loc))

    if (!locationId) {
      return
    }

    this.formService.setFacility(this.form, locationId)
    const estimate = this.state.estimate()
    if (estimate && !estimate.facilityId) {
      this.state.setEstimate({ ...estimate, facilityId: locationId })
    }
  }

  loadAppointmentsForPatient(patientId: string): Observable<AppointmentModel[]> {
    return this.api.getAppointmentsForPatient({ patientId, extend: 'products,estimates,physician' }).pipe(
      tap(appointments => this.state.setAppointments(appointments)),
    )
  }

  // Stamp the patient on the unsaved draft so a downstream setEstimate that drops patientId
  // doesn't strand the save payload without one.
  setDraftPatient(patientId: string): void {
    this.state.setPatientId(patientId)
    const estimate = this.state.estimate()
    if (estimate && !estimate.id && estimate.patientId !== patientId) {
      this.state.setEstimate({ ...estimate, patientId })
    }
  }

  loadTemplate(templateId: string): Observable<EstimateTemplateModel> {
    return this.templateService.loadTemplate(templateId).pipe(
      tap(template => {
        const charges = this.chargeFormService.ensureVisitNumbers(template.charges ?? [])
        this.applyCharges(charges)
        if (this.form && template.facilityIds?.length) {
          this.formService.setFacility(this.form, template.facilityIds[0])
        }
      }),
    )
  }

  replaceCharges(charges: EstimateCharge[]): void {
    this.applyCharges(charges)
  }

  clearCharges(): void {
    if (this.form) {
      this.formService.clearCharges(this.form)
    }
    const estimate = this.state.estimate()
    if (estimate) {
      this.state.setEstimate({ ...estimate, charges: [], templateId: null })
    }
    this.state.markDirty()
  }

  save(): Observable<EstimateData> {
    const estimate = this.state.estimate()
    if (!estimate) {
      return throwError(() => new Error('No estimate to save'))
    }
    const isCreate = !estimate.id
    const payload = this.buildSavePayload(estimate)
    const op$ = estimate.id
      ? this.api.updateEstimate(estimate.id, payload)
      : this.api.createEstimate(payload)
    return op$.pipe(
      tap(saved => {
        this.state.setEstimate(saved)
        this.persistedAppointmentIds = [...(saved.appointmentIds ?? [])]
        this.state.markSaved()
        if (this.form) {
          this.formService.patchEstimateForm(this.form, saved)
        }
        // Creating an estimate adds a new "estimate" document — refresh the patient panels
        // (sidebar card, appointment Add/View Estimate button) without a reload (PIA-2567).
        if (isCreate) {
          this.balanceEvents.notifyBalancesChanged({ appointmentEstimateLinkChanged: true })
        }
      }),
    )
  }

  finalize(): Observable<EstimateBalance> {
    const estimateId = this.state.estimate()?.id
    if (!estimateId) {
      return throwError(() => new Error('Cannot finalize without an estimate id'))
    }
    return this.api.finalize(estimateId).pipe(
      tap(balance => {
        this.state.setBalance(balance)
        // Server clears the modify session on finalize; mirror locally for the next Modify click.
        this.state.setModifying(false)
        // Finalize flips the balance to CONFIRMED — refresh the patient balance panels (PIA-2574).
        this.balanceEvents.notifyBalancesChanged()
      }),
    )
  }

  startModify(): Observable<EstimateCalculateResult> {
    const estimateId = this.state.estimate()?.id
    if (!estimateId) {
      return throwError(() => new Error('Cannot start modify without an estimate id'))
    }
    return this.api.modifyStart(estimateId).pipe(
      tap(result => {
        this.state.setEstimate(result.estimate)
        this.state.setBalance(result.balance)
        this.state.setModifying(true)
        this.state.markDirty()
        // Modify reverts the balance to DRAFT, same as revert() (PIA-2574).
        this.balanceEvents.notifyBalancesChanged()
      }),
      switchMap(result => this.refreshBenefitsForSelection().pipe(map(() => result))),
    )
  }

  private refreshBenefitsForSelection(): Observable<unknown> {
    const refreshes: Array<Observable<EligibilityBenefits | null>> = []
    const primary = this.state.primaryInsurance()
    const secondary = this.state.secondaryInsurance()
    if (primary?.eligibilityId) {
      refreshes.push(this.refreshBenefits('primary', primary))
    }
    if (secondary?.eligibilityId) {
      refreshes.push(this.refreshBenefits('secondary', secondary))
    }
    if (refreshes.length === 0) {
      return of(null)
    }
    return forkJoin(refreshes).pipe(catchError(() => of(null)))
  }

  discardModify(): Observable<EstimateCalculateResult> {
    const estimateId = this.state.estimate()?.id
    if (!estimateId) {
      return throwError(() => new Error('Cannot discard modify without an estimate id'))
    }
    return this.api.discardModify(estimateId).pipe(
      tap(result => {
        this.state.setEstimate(result.estimate)
        this.state.setBalance(result.balance)
        this.state.setModifying(false)
        this.state.clearDirty()
        // Re-sync the reactive forms from the restored snapshot. The state signals roll back,
        // but the charge/benefit form controls otherwise keep the user's in-session edits until
        // a page refresh re-hydrates them (mirrors hydrate()/save()).
        if (this.form) {
          this.formService.patchEstimateForm(this.form, result.estimate)
          // patchEstimateForm skips charges when the restored value is null (multi-visit
          // snapshots store estimate.charges as null). Clear the FormArray explicitly so a
          // discarded in-session charge edit can't linger. setCharges([]) empties it without
          // the templateId side-effect that clearCharges() would apply.
          if (result.estimate.charges == null) {
            this.formService.setCharges(this.form, [])
          }
        }
        if (this.benefitsForm) {
          // Fall back to defaults when the restored estimate carries no inputs (legacy rows)
          // so the benefits controls always revert instead of keeping the in-session edits.
          this.formService.patchBenefitsForm(this.benefitsForm, result.estimate.inputs ?? this.formService.defaultInputs())
        }
        // Discard restores the balance to CONFIRMED (PIA-2574).
        this.balanceEvents.notifyBalancesChanged()
      }),
    )
  }

  saveAndCalculate(): Observable<EstimateCalculateResult> {
    // No id guard — save() routes a draft to createEstimate, which gives calculate() an id
    // via state. Matches legacy: Calculate creates-then-calculates in one click.
    const estimate = this.state.estimate()
    const currentAppointmentIds = estimate?.appointmentIds ?? []
    // A CoC appointment change is committed here, at Calculate time — not on dropdown toggle.
    // PUT /estimates rejects CoC appointment edits, so commit them via POST /appointments first,
    // then save() persists the charge/benefit edits, then calculate() recomputes the balance.
    const needsAppointmentCommit =
      estimate?.id != null &&
      this.state.cocMode() &&
      !this.appointmentIdsEqual(currentAppointmentIds, this.persistedAppointmentIds)
    const persisted$ = needsAppointmentCommit
      ? this.commitAppointmentChange(currentAppointmentIds).pipe(switchMap(() => this.save()))
      : this.save()
    return persisted$.pipe(switchMap(() => this.calculate()))
  }

  revert(): Observable<EstimateBalance> {
    const estimateId = this.state.estimate()?.id
    if (!estimateId) {
      return throwError(() => new Error('Cannot revert without an estimate id'))
    }
    return this.api.revertToDraft(estimateId).pipe(
      tap(balance => {
        this.state.setBalance(balance)
        // Revert flips the balance back to DRAFT — refresh the patient balance panels.
        this.balanceEvents.notifyBalancesChanged()
      }),
    )
  }

  delete(): Observable<void> {
    const estimateId = this.state.estimate()?.id
    if (!estimateId) {
      return throwError(() => new Error('Cannot delete without an estimate id'))
    }
    return this.api.deleteEstimate(estimateId).pipe(
      tap(() => {
        this.state.resetForNewEstimate()
        this.balanceEvents.notifyBalancesChanged({ appointmentEstimateLinkChanged: true })
      }),
    )
  }

  resetForNewEstimate(): void {
    this.state.resetForNewEstimate()
    if (this.form) {
      this.formService.clearCharges(this.form)
    }
  }

  // Snapshot + download — re-uses the persisted PDF so print can't drift from what was sent earlier.
  printLetter(): Observable<SendBalanceMessageResult> {
    const estimateId = this.state.estimate()?.id
    if (!estimateId) {
      return throwError(() => new Error('Cannot print without an estimate id'))
    }
    return this.api.sendBalanceMessage(estimateId, { method: 'download', what: 'pdf' }).pipe(
      tap(result => {
        this.state.setBalance(result.balance)
        this.fileService.saveAs({ url: result.snapshot.url, pdfFileName: `balance-${result.balance.id}.pdf` })
      }),
    )
  }

  sendLetter(channel: SendLetterChannel, what: SendBalanceWhat, patient: Patient | null): Observable<SendBalanceMessageResult> {
    const estimateId = this.state.estimate()?.id
    if (!estimateId) {
      return throwError(() => new Error('Cannot send without an estimate id'))
    }
    return this.sendContactEditDialog.open({
      method: channel,
      email: patient?.email,
      phone: patient?.phone,
    }).pipe(
      filter((result): result is SendContactEditDialogResult => result != null),
      switchMap(result => this.api.sendBalanceMessage(estimateId, {
        method: channel,
        what,
        to: result.to,
      })),
      tap(result => this.state.setBalance(result.balance)),
    )
  }

  private createDraftEstimate(seed?: Partial<EstimateData>): EstimateData {
    // Seed inputs so the save payload always carries them — undefined cascades to NaN downstream.
    return {
      patientId: '',
      facilityId: '',
      appointmentIds: [],
      charges: [],
      inputs: this.formService.defaultInputs(),
      ...seed,
    }
  }

  private loadBalanceAndVisits(estimate: EstimateData): Observable<{ balance: EstimateBalance | null; visits: EstimateVisit[] }> {
    if (!estimate.id || !estimate.estimateBalanceId) {
      return of({ balance: null, visits: [] })
    }
    return forkJoin({
      balance: this.api.getBalance(estimate.id),
      visits: this.api.getVisits(estimate.id),
    })
  }

  private refreshBenefits(
    slot: BenefitSlot,
    insurance: PatientInsurance | null,
    policyChanged = false,
  ): Observable<EligibilityBenefits | null> {
    const loadingSetter = slot === 'primary'
      ? (loading: boolean) => this.state.setPrimaryBenefitsLoading(loading)
      : (loading: boolean) => this.state.setSecondaryBenefitsLoading(loading)
    const benefitsSetter = slot === 'primary'
      ? (b: EligibilityBenefits | null) => this.state.setPrimaryBenefits(b)
      : (b: EligibilityBenefits | null) => this.state.setSecondaryBenefits(b)
    const selected = () => slot === 'primary' ? this.state.primaryInsurance() : this.state.secondaryInsurance()
    // A response that lands after the slot moved to another insurance must not be applied to it.
    const isCurrent = () => {
      const current = selected()
      return current?.id === insurance?.id && current?.eligibilityId === insurance?.eligibilityId
    }

    if (!insurance?.eligibilityId) {
      loadingSetter(false)
      if (policyChanged || insurance === null) {
        benefitsSetter(null)
        this.applyBenefits(slot, null)
      }
      return of(null)
    }
    loadingSetter(true)
    return this.api.getBenefits(insurance.eligibilityId).pipe(
      tap(benefits => {
        if (!isCurrent() || (benefits == null && !policyChanged)) {
          return
        }
        benefitsSetter(benefits)
        this.applyBenefits(slot, benefits)
      }),
      finalize(() => {
        if (isCurrent()) {
          loadingSetter(false)
        }
      }),
    )
  }

  /**
   * Folds an eligibility response into estimate.inputs plus the matching originalBenefits snapshot;
   * the container's effect then patches the form off the new inputs reference.
   *
   * Reads inputs off state, not the benefits form — that form is patched by the same effect, which
   * has not necessarily run when this lands during hydrate.
   */
  private applyBenefits(slot: BenefitSlot, benefits: EligibilityBenefits | null): void {
    const estimate = this.state.estimate()
    if (!estimate || !this.canApplyBenefits()) {
      return
    }
    const snapshot = slot === 'primary' ? estimate.originalBenefits : estimate.originalSecondaryBenefits
    const result = syncBenefitsIntoInputs(
      estimate.inputs ?? this.formService.defaultInputs(),
      snapshot,
      benefits,
      slot,
    )
    if (!result.changed) {
      return
    }
    this.state.setEstimate(slot === 'primary'
      ? { ...estimate, inputs: result.inputs, originalBenefits: result.snapshot }
      : { ...estimate, inputs: result.inputs, originalSecondaryBenefits: result.snapshot })
    // A draft has nothing persisted to diverge from, and a snapshot-only refresh fires on every
    // hydrate. Marking dirty for either prompts CanDeactivate over values nobody typed.
    if (result.inputsChanged && estimate.id != null) {
      this.state.markDirty()
    }
  }

  private applyCharges(charges: EstimateCharge[]): void {
    const normalized = this.normalizeChargeVisitNumbers(charges)
    if (this.form) {
      this.formService.setCharges(this.form, normalized)
    }
    const estimate = this.state.estimate()
    if (estimate) {
      this.state.setEstimate({ ...estimate, charges: normalized })
    }
    this.state.markDirty()
  }

  private canApplyBenefits(): boolean {
    const balance = this.state.balance()
    return balance == null || balance.workflowStatus === EstimateBalanceWorkflowStatus.DRAFT
  }

  /**
   * Fills in visit numbers before charges fan out to both the form and the estimate state, keeping
   * them in sync. Charges sourced outside the appointment flow (e.g. Model Claim Search) carry no
   * visitNumbers, which the Course-of-Care summary needs to map CPT codes to visits. The appointment
   * count falls back to the estimate state so normalization stays correct even when the form isn't
   * attached yet (or in non-component usage).
   */
  private normalizeChargeVisitNumbers(charges: EstimateCharge[]): EstimateCharge[] {
    const formCount = this.form?.controls.appointmentIds.value?.length ?? 0
    const stateCount = this.state.estimate()?.appointmentIds?.length ?? 0
    const appointmentCount = Math.max(formCount, stateCount)
    return this.chargeFormService.ensureVisitNumbers(charges, appointmentCount)
  }

  private buildSavePayload(estimate: EstimateData): Partial<EstimateData> {
    const formValues = this.form ? this.formService.getPayloadValues(this.form) : null
    const isUnscheduled = this.state.unscheduled()
    // Preserve visitNumbers in CoC or unscheduled; clear them otherwise.
    const preserveVisitNumbers = this.state.cocMode() || isUnscheduled
    const charges = this.form
      ? this.formService.buildSaveChargesPayload(this.form, preserveVisitNumbers)
      : (estimate.charges ?? [])
    // Fall back to estimate.inputs when the benefits form isn't attached (unit tests skip it).
    const inputs = this.benefitsForm
      ? this.formService.benefitsFormToInputs(this.benefitsForm)
      : estimate.inputs
    const appointmentIds = isUnscheduled
      ? []
      : (formValues?.appointmentIds ?? estimate.appointmentIds)
    const mode = isUnscheduled ? Estimate.MODE.UNSCHEDULED : Estimate.MODE.SCHEDULED
    return {
      ...estimate,
      // Fall back to state.patientId on direct /estimates/new — the draft seed may not have one.
      patientId: estimate.patientId || this.state.patientId() || '',
      facilityId: formValues?.facilityId ?? estimate.facilityId,
      templateId: formValues?.templateId ?? estimate.templateId,
      appointmentIds,
      patientType: formValues?.patientType ?? estimate.patientType,
      // UI flag picks mode on a draft; backend rejects flips on persisted estimates (mode is
      // immutable once set — surfaces via snackbar on the 400).
      mode,
      charges,
      inputs,
      // Must survive the round trip, or every reload re-derives from a null snapshot and re-badges
      // untouched fields as modified.
      originalBenefits: estimate.originalBenefits,
      originalSecondaryBenefits: estimate.originalSecondaryBenefits,
      // Required — without it the backend defaults insuranceType to selfpay (zero insurance resp).
      insurances: this.buildInsurancesPayload(inputs, estimate.patientId || this.state.patientId() || ''),
    }
  }

  // State is the wire — seeded on hydrate by EstimateInsuranceLoader, mutated by picker changes
  // and setHasSecondaryInsurance(false). Straight serialisation, no fallback merge, so a half-set
  // selection can't silently re-emit the stale half of `estimate.insurances`.
  private buildInsurancesPayload(inputs: EstimateData['inputs'], patientId: string): EstimateData['insurances'] {
    const primary = this.insuranceBelongsToPatient(this.state.primaryInsurance(), patientId)
      ? this.state.primaryInsurance()
      : null
    const secondary = inputs?.hasSecondary !== false
      && this.insuranceBelongsToPatient(this.state.secondaryInsurance(), patientId)
      ? this.state.secondaryInsurance()
      : null
    if (!primary && !secondary) {
      return undefined
    }
    return {
      P: primary ? this.toEstimateInsurance(primary) : undefined,
      S: secondary ? this.toEstimateInsurance(secondary) : undefined,
    }
  }

  private toEstimateInsurance(ins: PatientInsurance): { insuranceId: string; eligibilityId: string } {
    // PatientInsurance.eligibilityId is optional but EstimateInsurance.eligibilityId is `string` — '' satisfies it.
    return { insuranceId: ins.id, eligibilityId: ins.eligibilityId ?? '' }
  }

  private insuranceBelongsToPatient(insurance: PatientInsurance | null, patientId: string): boolean {
    return !insurance?.patientId || !patientId || insurance.patientId === patientId
  }

}
